import { Clapperboard, LayoutDashboard, Music2 } from "lucide-react";

import { ThemeToggle } from "@/components/landing/theme-toggle";
import { ModelSelector } from "@/components/model-selector";
import { ChatShareMenu } from "@/components/playground/chat-share-menu";
import { PromoModelBadge } from "@/components/playground/promo-model-badge";
import { Label } from "@/components/ui/label";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { Switch } from "@/components/ui/switch";

import type { ModelDefinition, ProviderDefinition } from "@routerbench/models";
import type { UIMessage } from "ai";

interface ChatHeaderProps {
	models: ModelDefinition[];
	providers: ProviderDefinition[];
	selectedModel: string;
	setSelectedModel: (model: string) => void;
	comparisonEnabled: boolean;
	onComparisonEnabledChange: (enabled: boolean) => void;
	showGlobalModelSelector: boolean;
	orgId?: string | null;
	messages?: UIMessage[];
	chatTitle?: string;
}

export const ChatHeader = ({
	models,
	providers,
	selectedModel,
	setSelectedModel,
	comparisonEnabled,
	onComparisonEnabledChange,
	showGlobalModelSelector,
	orgId,
	messages,
	chatTitle,
}: ChatHeaderProps) => {
	return (
		<header className="flex items-center h-14 px-5 border-b border-border/60">
			<div className="flex items-center gap-3 min-w-0 flex-1">
				<SidebarTrigger className="shrink-0" />
				<div className="h-5 w-px bg-border/60 hidden sm:block" />
				<nav className="hidden items-center gap-1 sm:flex">
					<a
						href={
							process.env.NODE_ENV === "development"
								? "http://localhost:3002/dashboard"
								: "https://app.routerbench.com/"
						}
						target="_blank"
						rel="noopener noreferrer"
						className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
					>
						<LayoutDashboard className="h-4 w-4" />
						Dashboard
					</a>
					<a
						href="/video"
						className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
					>
						<Clapperboard className="h-4 w-4" />
						Video
					</a>
					<a
						href="/music"
						className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
					>
						<Music2 className="h-4 w-4" />
						Music
					</a>
				</nav>
				{showGlobalModelSelector ? (
					<>
						<div className="h-5 w-px bg-border/60 hidden sm:block" />
						<div className="flex items-center gap-2 w-full max-w-[340px] sm:max-w-[400px] min-w-0">
							<ModelSelector
								models={models}
								providers={providers}
								value={selectedModel}
								onValueChange={setSelectedModel}
								placeholder="Search models..."
							/>
							<PromoModelBadge orgId={orgId} selectedModel={selectedModel} />
						</div>
					</>
				) : null}
			</div>
			<div className="flex items-center gap-2 ml-2 shrink-0">
				{messages && messages.length > 0 ? (
					<ChatShareMenu
						messages={messages}
						title={chatTitle ?? "Conversation"}
					/>
				) : null}
				<div className="hidden md:flex items-center gap-2 mr-1">
					<Label
						htmlFor="comparison-mode"
						className="text-xs text-muted-foreground"
					>
						Compare
					</Label>
					<Switch
						id="comparison-mode"
						checked={comparisonEnabled}
						onCheckedChange={onComparisonEnabledChange}
					/>
				</div>
				<ThemeToggle />
			</div>
		</header>
	);
};
